import clsx from 'clsx';
import { CornerDownRight } from 'lucide-react';
import type { DiffKind, Hit } from '@/lib/checkpoint/types';

const STATE_TONE: Record<string, string> = {
  pass: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  mid: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  fail: 'border-rose-500/40 bg-rose-500/10 text-rose-200',
  timeout: 'border-rose-500/40 bg-rose-500/10 text-rose-200',
};

/**
 * diff token 类型 → tooltip 文案映射。骨架对齐命中后，每个 diff 位置会被
 * `classifyToken` 打成一个 DiffKind，UI 在悬停时把它翻译成中文标签。
 */
const KIND_LABEL: Record<DiffKind, string> = {
  number: '数字变量',
  hex: '十六进制',
  quoted: '引号包裹值',
  activity: 'Activity 名',
  dotted: '包名',
  mixed: '混合变量',
  any: '任意 token',
};

export function HitDetail({ hit, onJump }: { hit: Hit; onJump?: () => void }) {
  const tone = STATE_TONE[hit.state] ?? 'border-line bg-bg-soft';
  const clickable = !!onJump && hit.lineId >= 0;
  return (
    <div
      className={clsx(
        'rounded-md border px-2 py-1.5 text-[11px]',
        tone,
        clickable && 'cursor-pointer transition hover:brightness-110',
      )}
      onClick={clickable ? onJump : undefined}
      role={clickable ? 'button' : undefined}
      title={clickable ? '点击跳到日志中的命中行' : undefined}
    >
      <div className="flex items-center justify-between gap-2 font-mono">
        <span>{hit.ts || '—'}</span>
        <span className="inline-flex items-center gap-1">
          {hit.state.toUpperCase()}
          {clickable && <CornerDownRight className="h-3 w-3" />}
        </span>
      </div>
      <div className="mt-1 break-all font-mono text-ink">{hit.summary}</div>
      <MatchModeChip hit={hit} />
      {hit.reason && (
        <div className="mt-1 text-ink-muted">{hit.reason}</div>
      )}
      {hit.raw && hit.raw !== hit.summary && (
        <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-all text-[10px] text-ink-dim">
          {hit.raw}
        </pre>
      )}
    </div>
  );
}

/**
 * 命中模式 chip + 骨架/启发详情。
 *
 * - mode='literal' → 灰底 chip 「字面」
 * - mode='fuzzy' & fuzzyKind='skeleton' → amber chip 「骨架 · ...keyword」
 *   并按 token 渲染命中行：固定 token 用 emerald 浅色、diff token 用
 *   amber 下划线高亮，悬停 tooltip 显示 DiffKind 标签。
 * - mode='fuzzy' & fuzzyKind='heuristic' → amber chip 「启发 · ...keyword」
 *   并显示展开后的 fuzzyPattern。
 */
function MatchModeChip({ hit }: { hit: Hit }): JSX.Element | null {
  if (!hit.mode) return null;
  const isSkeleton = hit.mode === 'fuzzy' && hit.fuzzyKind === 'skeleton';
  const isHeuristic = hit.mode === 'fuzzy' && hit.fuzzyKind === 'heuristic';
  const chipText =
    hit.mode === 'literal'
      ? '字面'
      : isSkeleton
        ? `骨架 · ${hit.matchedKeyword ?? ''}`
        : `启发 · ${hit.matchedKeyword ?? ''}`;
  const chipCls =
    hit.mode === 'literal'
      ? 'bg-bg-soft text-ink-muted'
      : 'bg-amber-500/20 text-amber-200';
  return (
    <div className="mt-1 space-y-1">
      <span
        className={clsx(
          'inline-block rounded px-1 py-0.5 font-mono text-[10px]',
          chipCls,
        )}
      >
        {chipText}
      </span>
      {isSkeleton &&
        hit.fuzzyLineTokens &&
        hit.fuzzyWindowStart !== undefined && (
          <SkeletonTokens hit={hit} />
        )}
      {isHeuristic && hit.fuzzyPattern && (
        <pre className="max-h-12 overflow-auto rounded bg-bg-soft px-1 py-0.5 font-mono text-[10px] text-ink-muted">
          展开 → {hit.fuzzyPattern}
        </pre>
      )}
    </div>
  );
}

function SkeletonTokens({ hit }: { hit: Hit }): JSX.Element {
  const tokens = hit.fuzzyLineTokens ?? [];
  const start = hit.fuzzyWindowStart ?? 0;
  const kwLen = hit.matchedKeyword
    ? hit.matchedKeyword.trim().split(/\s+/).filter(Boolean).length
    : 0;
  const diffs = hit.fuzzyDiffs ?? [];
  const diffKinds = hit.fuzzyDiffKinds ?? [];
  return (
    <div className="flex flex-wrap items-baseline gap-x-0 gap-y-1 font-mono text-[10px]">
      {tokens.map((tok, i) => {
        const inWindow = i >= start && i < start + kwLen;
        const localIdx = i - start;
        const diffPos = diffs.indexOf(localIdx);
        const isDiff = inWindow && diffPos >= 0;
        const kind = isDiff ? diffKinds[diffPos] : undefined;
        return (
          <span key={i}>
            <span
              className={clsx(
                isDiff &&
                  'rounded bg-amber-500/30 px-0.5 text-amber-100 underline decoration-amber-300 decoration-dotted',
                inWindow && !isDiff && 'text-emerald-200',
              )}
              title={kind ? `${KIND_LABEL[kind]}（不同位置）` : undefined}
            >
              {tok}
            </span>
            {i < tokens.length - 1 && <span>{' '}</span>}
          </span>
        );
      })}
    </div>
  );
}
